// Reads the loaded history entry before the router starts (usePageArrival).
import "./lib/document-entry";
import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { initPerformanceMonitoring, startPerformanceMark } from "./lib/performance";
import { initMonitoring } from "./monitoring/sentry.ts";
import "./index.css";
import { rememberUiChoice } from "./lib/ui-uplift";

// Polyfill crypto.randomUUID for older browsers
if (typeof crypto !== "undefined" && typeof crypto.randomUUID !== "function") {
  crypto.randomUUID = function () {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
      (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
    );
  };
}

// Force rebuild to clear cached bundle with options error - v2
initMonitoring();
const appBootstrapMark = startPerformanceMark("app:bootstrap");
initPerformanceMonitoring({
  enableProductionLogging: import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING === "true",
});

// Capture a `?ui=next|classic` choice before any redirect can drop the query.
rememberUiChoice(window.location.search);

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Self-heal stale tabs after a deploy. Vite emits `vite:preloadError` when a
// dynamic-import chunk 404s — typically a tab held open across a deploy whose
// old asset hashes no longer exist. Root wrangler.jsonc uses SPA fallback, so
// the 404 returns index.html, the browser parses HTML as JS, and the tab
// white-screens with `Unexpected token '<'`. One forced reload pulls the fresh
// index.html (and its current chunk graph), then the tab keeps going.
//
// Loop guard: we record the TIMESTAMP of the last self-heal reload — not a flag
// cleared once the app mounts. A lazy-route chunk can fail well after the
// initial mount (e.g. while ProtectedRoute is still awaiting auth/org/MFA), and
// a mount-cleared flag would let a genuinely-missing chunk reload forever. If
// another preloadError fires within CHUNK_RELOAD_TTL_MS of the last reload,
// we've already pulled fresh index.html and the chunk is still gone — a real
// CDN/build failure, not deploy skew — so we stop and let the error surface
// instead of looping. A later deploy (past the TTL) can self-heal again.
// sessionStorage can throw (sandboxed/blocked storage, some private modes), so
// every access is guarded and falls back to an in-memory one-shot.
const CHUNK_RELOAD_KEY = "forge:chunk-reloaded-at";
const CHUNK_RELOAD_TTL_MS = 10_000;
let chunkReloadedInMemory = false;

function shouldHealStaleChunk(): boolean {
  const now = Date.now();
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
    if (last && now - last < CHUNK_RELOAD_TTL_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
    return true;
  } catch {
    // Storage unavailable/blocked — still heal once via an in-memory guard
    // rather than leaving the user staring at a white screen.
    if (chunkReloadedInMemory) return false;
    chunkReloadedInMemory = true;
    return true;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", event => {
    if (!shouldHealStaleChunk()) return;
    event.preventDefault();
    window.location.reload();
  });

  window.requestAnimationFrame(() => {
    appBootstrapMark.end();
  });
}

// Fade out and remove the inline splash screen after React has mounted
const splash = document.getElementById("app-splash");
if (splash) {
  let splashRemoved = false;
  const removeSplash = () => {
    if (splashRemoved) return;
    splashRemoved = true;
    splash.remove();
  };

  splash.setAttribute("data-hidden", "");
  splash.addEventListener("transitionend", removeSplash, { once: true });
  // Reduced-motion settings, cached styles, or a failed transition event
  // must not leave the inline splash over the mounted React app.
  window.setTimeout(removeSplash, 500);
}

// ─── Service Worker Registration + Update Lifecycle ──────────────────────────
// Skip on native — WKWebView/Android WebView don't support service workers and
// the registration attempt can cause timing issues or silent errors.
import { Capacitor } from "@capacitor/core";
if ("serviceWorker" in navigator && !Capacitor.isNativePlatform()) {
  navigator.serviceWorker.register("/sw.js").then((registration) => {
    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener("statechange", () => {
          if (
            newWorker.state === "activated" &&
            navigator.serviceWorker.controller
          ) {
            // New version available — inform the user via console for now
            // (will be wired to toast system in app context)
            console.log("[SW] New version available. Refresh to update.");
          }
        });
      }
    });
  });
}
